import { useMutation, useQuery } from '@tanstack/react-query';
import { useAuth } from '../contexts/authContext';
import { ragClient } from '../lib/connect/clients';
// ============================================================================
// API Functions
// ============================================================================
// These call cp0 directly. express only ever stood in the middle reshaping
// JSON: it made the same three Connect calls and then rebuilt this shape from
// the response, because SearchResult carries id, content and score as fields
// and everything else inside its metadata Struct
// (searchDocuments.handler.ts:62). The unpacking below is that same mapping,
// moved to the one place that actually wants this shape.
//
// Identity is not sent. cp0 binds user_context to the gateway principal and
// ignores what the body claims, so a client-supplied one would be overwritten.
function metaOf(result) {
    return result.metadata ? result.metadata.toJson() : {};
}
function toDocument(result, tenantId) {
    const meta = metaOf(result);
    const now = new Date().toISOString();
    return {
        id: result.id,
        tenant_id: tenantId,
        title: meta.title || 'Untitled',
        content: result.content,
        source: meta.source || '',
        source_type: meta.source_type || 'unknown',
        summary: meta.summary,
        relevance_score: result.score,
        permission_score: meta.permission_score,
        metadata: meta,
        highlights: Array.isArray(meta.highlights) ? meta.highlights : undefined,
        created_at: meta.created_at || now,
        updated_at: meta.updated_at || now,
        processed_at: meta.processed_at
    };
}
// filtered_by_permissions, processing_time_ms and cache_hit are not in the
// proto. express filled them with 0/0/false rather than leaving them out, so
// the same constants keep the response shape stable for existing consumers.
function toSearchResponse(results, total, requestId, tenantId) {
    return {
        documents: results.map((r)=>toDocument(r, tenantId)),
        total,
        filtered_by_permissions: 0,
        query_id: requestId,
        processing_time_ms: 0,
        cache_hit: false,
        metadata: {}
    };
}
async function searchDocuments(request, tenantId) {
    const response = await ragClient.search({
        query: request.query,
        limit: request.top_k ?? 0
    });
    return toSearchResponse(response.results, response.total, response.requestId, tenantId);
}
async function findSimilar(documentId, tenantId, topK) {
    const response = await ragClient.searchSimilar({
        documentId,
        limit: topK ?? 5
    });
    // SearchSimilarResponse carries no total — express reported the result count
    // here, so the same is reported rather than inventing a different meaning.
    return toSearchResponse(response.results, response.results.length, response.requestId, tenantId);
}
async function getDocument(documentId, tenantId) {
    const response = await ragClient.getDocument({
        id: documentId
    });
    return toDocument({
        id: response.id,
        content: response.content,
        score: 0,
        metadata: response.metadata
    }, tenantId);
}
// ============================================================================
// Hooks
// ============================================================================
/**
 * Hook for searching documents with RAG
 *
 * @example
 * ```tsx
 * const { search, isSearching, data, error } = useDocumentSearch();
 *
 * const handleSearch = () => {
 *   search({
 *     query: 'quarterly revenue report',
 *     top_k: 10,
 *     options: { include_content: true }
 *   });
 * };
 * ```
 */ export function useDocumentSearch() {
    const { isAuthenticated, user } = useAuth();
    // express stamped tenant_id onto every document from its own session; the
    // proto does not carry it, so it comes from the auth context instead.
    const tenantId = user?.tenant_id ?? '';
    const searchMutation = useMutation({
        mutationFn: (request)=>searchDocuments(request, tenantId),
        onError: (error)=>{
            console.error('Document search failed:', error);
        }
    });
    return {
        search: searchMutation.mutate,
        searchAsync: searchMutation.mutateAsync,
        isSearching: searchMutation.isPending,
        error: searchMutation.error,
        data: searchMutation.data,
        reset: searchMutation.reset,
        isAuthenticated
    };
}
/**
 * Hook for finding similar documents
 *
 * @param documentId - Document ID to find similar documents for
 * @param topK - Number of similar documents to return
 *
 * @example
 * ```tsx
 * const { data, isLoading, error } = useSimilarDocuments('doc-123', 5);
 * ```
 */ export function useSimilarDocuments(documentId, topK) {
    const { isAuthenticated, user } = useAuth();
    const tenantId = user?.tenant_id ?? '';
    return useQuery({
        queryKey: [
            'similar-documents',
            documentId,
            topK
        ],
        queryFn: ()=>{
            if (!documentId) throw new Error('documentId is required');
            return findSimilar(documentId, tenantId, topK);
        },
        enabled: !!documentId && isAuthenticated,
        staleTime: 5 * 60 * 1000,
        gcTime: 10 * 60 * 1000
    });
}
/**
 * Hook for getting a specific document by ID
 *
 * @param documentId - Document ID to retrieve
 *
 * @example
 * ```tsx
 * const { data, isLoading, error } = useDocument('doc-123');
 * ```
 */ export function useDocument(documentId) {
    const { isAuthenticated, user } = useAuth();
    const tenantId = user?.tenant_id ?? '';
    return useQuery({
        queryKey: [
            'document',
            documentId
        ],
        queryFn: ()=>{
            if (!documentId) throw new Error('documentId is required');
            return getDocument(documentId, tenantId);
        },
        enabled: !!documentId && isAuthenticated,
        staleTime: 5 * 60 * 1000,
        gcTime: 10 * 60 * 1000
    });
}
